// Logique de rapprochement de medicaments partagee entre
// pages/tools/DrugDuplicateChecker.js (verificateur public, cote patient)
// et pages/DoctorDashboard.js (redaction d'ordonnance, cote medecin) —
// extraite ici le 2026-09-05 pour que les deux endroits appliquent
// exactement les memes regles (aucune divergence de comportement entre
// "ce que le patient peut verifier lui-meme" et "ce que le medecin voit
// en prescrivant").
//
// Deux niveaux, INTENTIONNELLEMENT distincts :
//  - computeDuplicateGroups  : doublon EXACT, meme `dci_code`.
//  - computeSameSubstanceGroups : meme SUBSTANCE (`dci_name`, apres
//    normalisation des parentheses redondantes), mais `dci_code`
//    differents -> dosages/formes differents. Volontairement moins
//    alarmiste : associer deux dosages d'une meme substance peut etre
//    clinique et voulu (schema de charge...), ce n'est pas
//    automatiquement une erreur contrairement a un vrai doublon exact.
//
// Voir le commentaire en tete de DrugDuplicateChecker.js pour le detail
// du contexte, des cas testes en production, et des limites acceptees
// (rapprochement uniquement sur egalite stricte post-normalisation, pas
// de correspondance floue entre sels/voies d'administration differents ;
// 2 codes sur 2056 avec une anomalie connue dans la nomenclature source).

// Retire d'un `dci_name` toute parenthese dont le contenu est deja une
// redite du reste du libelle (ex. "AMOXICILLINE SODIQUE (AMOXICILLINE)"
// -> "AMOXICILLINE SODIQUE") — jamais une parenthese qui apporte une
// precision reelle (forme saline, voie d'administration...).
export const normalizeSubstanceName = (name) => {
  if (!name) return null;
  let result = name;
  for (let guard = 0; guard < 10; guard += 1) {
    const parens = [...result.matchAll(/\(([^()]+)\)\*{0,2}/g)];
    let strippedOne = false;
    for (const m of parens) {
      const inner = m[1].trim().toUpperCase();
      const without = result.slice(0, m.index) + result.slice(m.index + m[0].length);
      if (inner && without.toUpperCase().includes(inner)) {
        result = without
          .replace(/\s*\/\s*/g, '/')
          .replace(/\s+/g, ' ')
          .replace(/^[\s/]+|[\s/]+$/g, '')
          .trim();
        strippedOne = true;
        break;
      }
    }
    if (!strippedOne) break;
  }
  return result.replace(/\s+/g, ' ').trim();
};

// items : tableau d'objets portant au moins `dci_code`. Retourne les
// groupes (>= 2 elements) partageant exactement le meme `dci_code`.
export const computeDuplicateGroups = (items) => {
  const byDci = {};
  items.forEach((d) => {
    if (!d.dci_code) return; // pas de code -> impossible de le rapprocher d'un autre en confiance
    byDci[d.dci_code] = byDci[d.dci_code] || [];
    byDci[d.dci_code].push(d);
  });
  return Object.entries(byDci).filter(([, drugs]) => drugs.length > 1);
};

// items : tableau d'objets portant `dci_code` et `dci_name`. Retourne les
// groupes [key, drugs, displayName] : meme substance (nom normalise) mais
// dci_code differents (donc pas deja couverts par computeDuplicateGroups).
export const computeSameSubstanceGroups = (items) => {
  const byNormName = {};
  items.forEach((d) => {
    if (!d.dci_name) return;
    const key = normalizeSubstanceName(d.dci_name);
    if (!key) return;
    byNormName[key] = byNormName[key] || [];
    byNormName[key].push(d);
  });
  return Object.entries(byNormName)
    .filter(([, drugs]) => new Set(drugs.map((d) => d.dci_code)).size > 1)
    .map(([key, drugs]) => {
      // Le libelle officiel exact peut differer legerement selon le code :
      // on affiche le plus complet des libelles rencontres dans ce groupe
      // plutot que la cle normalisee (interne).
      const displayName = drugs.reduce(
        (longest, d) => (d.dci_name.length > longest.length ? d.dci_name : longest),
        drugs[0].dci_name
      );
      return [key, drugs, displayName];
    });
};
